import React, { useMemo, useCallback } from 'react'

import { uniqueId } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import {
  isMultiValueOperator,
  OPERATOR_DEFINITIONS,
  OperatorType,
} from './config'
import { FilterConditionRow } from './FilterConditionRow'
import {
  FilterStructure,
  FilterGroup,
  FilterCondition,
  AvailableEntity,
} from './types'
import { updateConditionInFilter } from './updateCondition'
import { buildAvailableEntities } from './utils/buildAvailableEntities'
import { calculateFieldUpdate } from './utils/fieldTypeCompatibility'
import { Button } from '@/Atomic/Molecules/Button'
import { Form, FormBody, FormGroup } from '@/Atomic/Templates/Form'
import { useEntitySchema } from '@/hooks/useEntitySchema'
import './FilterBuilder.scss'

export interface FilterBuilderProps {
  /** The filter structure to display/edit */
  filter: FilterStructure
  /** Callback when filter changes */
  onChange: (filter: FilterStructure) => void
  /** The base entity name (e.g., 'person', 'company') */
  entityName: string
  /** Optional class name for styling */
  className?: string
  /** Validation errors */
  errors?: {
    field: Set<string>
    value: Set<string>
  }
  /** Callback to clear error */
  onClearError?: (type: 'field' | 'value', id: string) => void
}

export const FilterBuilder: React.FC<FilterBuilderProps> = connect(
  ({
    filter,
    onChange,
    entityName,
    className,
    errors,
    onClearError,
  }: FilterBuilderProps) => {
    const { t } = useTranslation()
    const { attributes: baseSchemaAttributes, relations: schemaRelations } =
      useEntitySchema(entityName)

    // Use passed errors or empty sets
    const fieldErrorConditionIds = errors?.field || new Set()
    const valueErrorConditionIds = errors?.value || new Set()

    const availableEntities = useMemo<AvailableEntity[]>(
      () =>
        buildAvailableEntities({
          entityName,
          baseSchemaAttributes,
          schemaRelations,
          t,
        }),
      [entityName, baseSchemaAttributes, schemaRelations, t],
    )

    // Clear field error for a specific condition
    const clearFieldError = useCallback(
      (conditionId: string) => {
        onClearError?.('field', conditionId)
      },
      [onClearError],
    )

    // Clear value error for a specific condition
    const clearValueError = useCallback(
      (conditionId: string) => {
        onClearError?.('value', conditionId)
      },
      [onClearError],
    )

    const createEmptyCondition = useCallback(
      (): FilterCondition => ({
        id: uniqueId('cond_'),
        entity: entityName,
        field: '',
        operator: 'eq',
        value: '',
      }),
      [entityName],
    )

    const createEmptyGroup = useCallback(
      (): FilterGroup => ({
        id: uniqueId('group_'),
        conditions: [createEmptyCondition()],
      }),
      [createEmptyCondition],
    )

    const handleAddGroup = (e?: React.MouseEvent) => {
      e?.preventDefault()
      const newGroup = createEmptyGroup()
      onChange({
        ...filter,
        groups: [...filter.groups, newGroup],
      })
    }

    const handleRemoveGroup = (groupId: string, e?: React.MouseEvent) => {
      e?.preventDefault()
      const remainingGroups = filter.groups.filter((g) => g.id !== groupId)

      onChange({
        ...filter,
        groups:
          remainingGroups.length > 0 ? remainingGroups : [createEmptyGroup()],
      })
    }

    const handleAddCondition = (
      groupId: string,
      afterConditionId?: string,
      e?: React.MouseEvent,
    ) => {
      e?.preventDefault()
      const newGroups = filter.groups.map((g) => {
        if (g.id === groupId) {
          const newCondition = createEmptyCondition()

          // If afterConditionId is provided, insert after that condition
          if (afterConditionId) {
            const conditionIndex = g.conditions.findIndex(
              (c) => c.id === afterConditionId,
            )
            if (conditionIndex !== -1) {
              const newConditions = [...g.conditions]
              newConditions.splice(conditionIndex + 1, 0, newCondition)
              return {
                ...g,
                conditions: newConditions,
              }
            }
          }

          // Otherwise, add at the end (fallback)
          return {
            ...g,
            conditions: [...g.conditions, newCondition],
          }
        }
        return g
      })

      onChange({
        ...filter,
        groups: newGroups,
      })
    }

    const handleRemoveCondition = (
      groupId: string,
      conditionId: string,
      e?: React.MouseEvent,
    ) => {
      e?.preventDefault()
      const groupIndex = filter.groups.findIndex((g) => g.id === groupId)
      if (groupIndex === -1) return

      const group = filter.groups[groupIndex]
      if (!group) return

      const remainingConditions = group.conditions.filter(
        (c) => c.id !== conditionId,
      )

      // If no conditions remain, remove the group
      if (remainingConditions.length === 0) {
        const remainingGroups = filter.groups.filter((g) => g.id !== groupId)

        onChange({
          ...filter,
          groups:
            remainingGroups.length > 0 ? remainingGroups : [createEmptyGroup()],
        })
        return
      }

      // Otherwise just update the group with remaining conditions
      const newGroups = [...filter.groups]
      newGroups[groupIndex] = {
        ...group,
        conditions: remainingConditions,
        id: group.id || uniqueId('group_'),
      }

      onChange({
        ...filter,
        groups: newGroups,
      })
    }

    const updateField = useCallback(
      (groupId: string, conditionId: string, field: string) => {
        onChange(
          updateConditionInFilter(filter, groupId, conditionId, (c) => {
            const { operator, value } = calculateFieldUpdate(
              c,
              field,
              baseSchemaAttributes,
            )

            return {
              ...c,
              field,
              operator,
              value,
            } as FilterCondition
          }),
        )
      },
      [filter, baseSchemaAttributes, onChange],
    )

    const updateOperator = useCallback(
      (groupId: string, conditionId: string, operator: string) => {
        onChange(
          updateConditionInFilter(filter, groupId, conditionId, (c) => {
            const oldOperator = c.operator as OperatorType
            const newOperator = operator as OperatorType

            // Get operator definitions
            const oldOperatorDef = OPERATOR_DEFINITIONS[oldOperator]
            const newOperatorDef = OPERATOR_DEFINITIONS[newOperator]

            // Preserve value if both operators require values or both don't
            // This allows switching between compatible operators like eq <-> neq, or empty <-> notEmpty
            const shouldPreserveValue =
              oldOperatorDef?.requiresValue === newOperatorDef?.requiresValue

            let nextValue = shouldPreserveValue ? c.value : null
            let nextLabel = c.label

            if (shouldPreserveValue) {
              if (
                isMultiValueOperator(newOperator) &&
                !Array.isArray(nextValue)
              ) {
                nextValue =
                  nextValue !== null &&
                  nextValue !== undefined &&
                  nextValue !== ''
                    ? [nextValue]
                    : []
              } else if (
                !isMultiValueOperator(newOperator) &&
                Array.isArray(nextValue)
              ) {
                const firstValue = nextValue[0]
                nextValue = firstValue ?? null
                nextLabel =
                  firstValue !== undefined ? c.label?.split(', ')[0] : undefined
              }
            } else {
              nextLabel = undefined
            }

            return {
              ...c,
              operator,
              value: nextValue,
              label: nextLabel,
            } as FilterCondition
          }),
        )
      },
      [filter, onChange],
    )

    const updateValue = useCallback(
      (
        groupId: string,
        conditionId: string,
        value: unknown,
        label?: string,
      ) => {
        onChange(
          updateConditionInFilter(filter, groupId, conditionId, (c) => ({
            ...c,
            value,
            ...(label !== undefined && { label }),
          })),
        )
      },
      [filter, onChange],
    )

    const handleEntityChange = useCallback(
      (
        groupId: string,
        conditionId: string,
        newEntity: string,
        referenceField?: string,
      ) => {
        onChange(
          updateConditionInFilter(filter, groupId, conditionId, (c) => ({
            ...c,
            entity: newEntity,
            referenceField: referenceField,
            field: '',
            operator: 'eq',
            value: null,
          })),
        )
      },
      [filter, onChange],
    )

    return (
      <div className={`filter-builder ${className || ''}`}>
        <Form>
          <FormBody>
            {filter.groups.map((group, groupIndex) => (
              <React.Fragment key={group.id}>
                {groupIndex > 0 && (
                  <div className="filter-separator">
                    <span>{t('filter.operator.or')}</span>
                  </div>
                )}
                <div className="filter-group">
                  <div className="group-header">
                    <h5>
                      {t(
                        'filter.section_conditions',
                        'Select items that match all conditions',
                      )}
                    </h5>
                    <Button
                      variant="icon"
                      icon="bin"
                      iconColor="danger"
                      size="sm"
                      tabIndex={-1}
                      onClick={(e) => handleRemoveGroup(group.id!, e)}
                      title={t('common.remove')}
                      ariaLabel={t('common.remove')}
                    />
                  </div>
                  <FormGroup>
                    {group.conditions.map((condition, conditionIndex) => (
                      <React.Fragment key={condition.id}>
                        <FilterConditionRow
                          condition={condition}
                          groupId={group.id!}
                          baseEntityName={entityName}
                          baseSchemaAttributes={baseSchemaAttributes}
                          availableEntities={availableEntities}
                          isFirstRow={conditionIndex === 0}
                          hasFieldError={fieldErrorConditionIds.has(
                            condition.id!,
                          )}
                          hasValueError={valueErrorConditionIds.has(
                            condition.id!,
                          )}
                          onClearFieldError={() =>
                            clearFieldError(condition.id!)
                          }
                          onClearValueError={() =>
                            clearValueError(condition.id!)
                          }
                          onEntityChange={handleEntityChange}
                          onFieldChange={updateField}
                          onOperatorChange={updateOperator}
                          onValueChange={updateValue}
                          onRemoveCondition={handleRemoveCondition}
                        />
                        {conditionIndex < group.conditions.length - 1 && (
                          <div className="filter-separator">
                            <span>{t('filter.operator.and')}</span>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </FormGroup>
                  <div className="group-footer">
                    <Button
                      variant="outlined"
                      icon="add"
                      size="sm"
                      onClick={(e) =>
                        handleAddCondition(group.id!, undefined, e)
                      }
                    >
                      {t('filter.add_condition')}
                    </Button>
                  </div>
                </div>
              </React.Fragment>
            ))}

            {filter.groups.length > 0 && (
              <div className="filter-separator">
                <span>{t('filter.operator.or')}</span>
              </div>
            )}
            <div className="add-group-container">
              <Button
                variant="outlined"
                icon="add"
                size="sm"
                onClick={(e) => handleAddGroup(e)}
              >
                {t('filter.add_criteria')}
              </Button>
            </div>
          </FormBody>
        </Form>
      </div>
    )
  },
)
